import * as React from 'react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'

import { Box, Button, TextBody } from '@ally/metronome-ui'
import { formatPhoneDefault } from '@ally/aos-utils'
import { Private } from '@ally/private'
import { EnrollConfirmationProps } from '@ally/transmitigator'
import { HostServices } from '@ally/federated-types'
import { LoginHeading } from '@ally/ally-login'

import { useTransmit } from '../../TransmitContext'
import { WelcomeSvg } from '../svgs'

const StyledBox = styled(Box)`
  display: flex;
  flex-direction: column;
`

const StyledBoxWrapper = styled(Box)`
  ${({ theme: { media } }): string => `
    max-width: 575px;
    align-self: center;
      order: 1;
      ${media.down('md')} {
        max-width: 450px;
        order: 2;
      }
    `}
`

const StyledSvgWrapper = styled(Box)`
  ${({ theme: { media } }): string => `
      width: 50%;
      max-width: 600px;
      order: 1;
      align-self: center;
      ${media.down('md')} {
        width: 215px;
      }
    `}
`

const StyledTextBody = styled(TextBody)`
  text-align: center;
`

export const ConfirmationContent: React.FC<
  EnrollConfirmationProps & {
    hostServices: HostServices | undefined
  }
> = ({
  onComplete,
  status,
  fullName,
  email,
  phone,
  username,
  userSelection,
  hostServices,
  shouldEndSession,
}) => {
  const history = useHistory()

  React.useEffect(() => {
    hostServices?.analytics.trackPageView({
      eventType: 'pageview',
      pageName: 'AOS:Enroll:All Set',
    })
  }, [])

  if (shouldEndSession) {
    history.push('/dashboard')
  }

  const handleConfirm = (): void => {
    onComplete({ userSelection })
  }

  return (
    <StyledBox data-testid="guest-login-page-wrapper">
      <StyledSvgWrapper data-testid="welcome-image">
        <WelcomeSvg />
      </StyledSvgWrapper>

      <StyledBoxWrapper data-testid="confirmation-wrapper">
        <LoginHeading title="You're all set!" />
      </StyledBoxWrapper>
      <StyledBoxWrapper data-testid="confirmation-wrapper">
        <Box mb="lg" display="flex">
          <StyledTextBody tag="p" size="md" data-testid="confirmation-subcopy">
            You can now manage your account online, making it easy to view
            account statements, set up alerts, update your security preferences,
            and more. To edit your information, choose <b>Get Started</b>, then
            visit <b>Profile and Settings</b>.
          </StyledTextBody>
        </Box>
      </StyledBoxWrapper>

      <StyledBoxWrapper>
        <Box mb="sm">
          <StyledTextBody tag="p" size="md" weight="bold">
            Name
          </StyledTextBody>
          <Private>
            <StyledTextBody tag="p" size="sm">
              {fullName}
            </StyledTextBody>
          </Private>
        </Box>
      </StyledBoxWrapper>

      <StyledBoxWrapper>
        <Box mb="sm">
          <StyledTextBody tag="p" size="md" weight="bold">
            Username
          </StyledTextBody>
          <Private>
            <StyledTextBody tag="p" size="sm">
              {username}
            </StyledTextBody>
          </Private>
        </Box>
      </StyledBoxWrapper>

      {email && (
        <StyledBoxWrapper>
          <Box mb="sm">
            <StyledTextBody tag="p" size="md" weight="bold">
              Email
            </StyledTextBody>
            <Private>
              <StyledTextBody tag="p" size="sm">
                {email}
              </StyledTextBody>
            </Private>
          </Box>
        </StyledBoxWrapper>
      )}

      {phone && (
        <StyledBoxWrapper>
          <Box mb="sm">
            <StyledTextBody tag="p" size="md" weight="bold">
              Phone Number
            </StyledTextBody>
            <Private>
              <StyledTextBody tag="p" size="sm">
                {formatPhoneDefault(phone)}
              </StyledTextBody>
            </Private>
          </Box>
        </StyledBoxWrapper>
      )}

      <StyledBoxWrapper data-testid="confirmation-wrapper">
        <Button
          allytmln="youreAllSet-getStarted"
          text="Get Started"
          fullWidthMd
          variant="primary"
          onClick={handleConfirm}
        />
      </StyledBoxWrapper>
    </StyledBox>
  )
}

export const Confirmation: React.FC = () => {
  const { activeScreen, hostServices } = useTransmit()

  if (activeScreen.type !== 'EnrollConfirmation') {
    return null
  }

  return (
    <ConfirmationContent
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...activeScreen.payload}
      hostServices={hostServices}
    />
  )
}
